/**
 * React context for MetaMask SDK + injected EVM wallet state.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { EvmChainDef } from "@/lib/bridgeNetworks";
import type { EthereumProvider } from "@/lib/evmWallet";
import {
  connectWallet,
  detectWalletLabel,
  disconnectWallet,
  getActiveConnector,
  getActiveProvider,
  getMetaMaskDeepLink,
  getWalletChainId,
  isMobileBrowser,
  switchOrAddChain,
  switchToMainnet,
  syncWalletSession,
  type ConnectWalletOptions,
  type WalletConnector,
} from "@/lib/walletConnect";

export type AetherionWalletState = {
  account: string | null;
  chainId: number | null;
  connector: WalletConnector;
  walletName: string;
  connecting: boolean;
  error: string | null;
  isMobile: boolean;
  deepLink: string;
  provider: EthereumProvider | null;
  connect: (options?: ConnectWalletOptions) => Promise<string>;
  disconnect: () => Promise<void>;
  refreshChainId: () => Promise<number | null>;
  switchToMainnet: () => Promise<number>;
  switchChain: (chain: EvmChainDef) => Promise<void>;
};

const WalletContext = createContext<AetherionWalletState | null>(null);

export function WalletProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<string | null>(null);
  const [chainId, setChainId] = useState<number | null>(null);
  const [connector, setConnector] = useState<WalletConnector>(null);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshChainId = useCallback(async () => {
    const id = await getWalletChainId();
    setChainId(id);
    return id;
  }, []);

  useEffect(() => {
    void (async () => {
      const addr = await syncWalletSession();
      if (addr) {
        setAccount(addr);
        setConnector(getActiveConnector());
        await refreshChainId();
      }
    })();
  }, [refreshChainId]);

  useEffect(() => {
    const eth = getActiveProvider();
    if (!eth?.on) return;
    const onAccounts = (...args: unknown[]) => {
      const accs = args[0] as string[] | undefined;
      setAccount(accs?.[0] ?? null);
      setConnector(getActiveConnector());
    };
    const onChain = () => {
      void refreshChainId();
    };
    eth.on("accountsChanged", onAccounts);
    eth.on("chainChanged", onChain);
    return () => {
      eth.removeListener?.("accountsChanged", onAccounts);
      eth.removeListener?.("chainChanged", onChain);
    };
  }, [account, refreshChainId]);

  const connect = useCallback(
    async (options?: ConnectWalletOptions) => {
      setConnecting(true);
      setError(null);
      try {
        const addr = await connectWallet(options);
        setAccount(addr);
        setConnector(getActiveConnector());
        await refreshChainId();
        return addr;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setError(msg);
        throw e;
      } finally {
        setConnecting(false);
      }
    },
    [refreshChainId],
  );

  const disconnect = useCallback(async () => {
    setError(null);
    await disconnectWallet();
    setAccount(null);
    setChainId(null);
    setConnector(null);
  }, []);

  const deepLinkPath =
    typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : "/bridge";

  const value = useMemo<AetherionWalletState>(
    () => ({
      account,
      chainId,
      connector,
      walletName: detectWalletLabel(getActiveProvider()),
      connecting,
      error,
      isMobile: isMobileBrowser(),
      deepLink: getMetaMaskDeepLink(deepLinkPath),
      provider: getActiveProvider(),
      connect,
      disconnect,
      refreshChainId,
      switchToMainnet,
      switchChain: switchOrAddChain,
    }),
    [account, chainId, connector, connecting, error, connect, disconnect, refreshChainId, deepLinkPath],
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export function useWalletContext(): AetherionWalletState {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error("useWalletContext must be used within WalletProvider");
  return ctx;
}
